import { memo, type ReactNode } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTypedSelector } from "hooks";
import { selectMerchantOptions } from "features/merchants/selectors";
import { selectConstants } from "features/general/selectors";
import type { WaMessageSetup } from "features/waMessaging/types";
import type { Option } from "types";
import { compareIds } from "util/support";
import { convertToOptions } from "util/transformers";

type Props = {
    readonly resource: WaMessageSetup;
};

const Row = ({ label, children }: { readonly label: string; readonly children: ReactNode }) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: '200px 1fr',
            alignItems: 'baseline',
            gap: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider'
        }}
    >
        <Typography variant='body2' color='text.secondary'>
            {label}
        </Typography>
        <Typography variant='body2'>
            {children ?? '—'}
        </Typography>
    </Box>
);

const formatStatuses = (value: WaMessageSetup['transactionStatuses']): string => {
    if (!value) return '';
    if (Array.isArray(value)) return value.join(', ');
    return value;
};

const WaMessageSetupView = ({ resource }: Props) => {
    const merchants = useTypedSelector(selectMerchantOptions);
    const { WaPhoneFrequencies } = useTypedSelector(selectConstants);

    const merchant = merchants.find((option: Option) => compareIds(option.id, resource.merchantId!));
    const frequencyOptions = convertToOptions(WaPhoneFrequencies ?? []);
    const frequency = frequencyOptions.find((option: Option) => option.id === resource.phoneFrequency);

    return (
        <Stack spacing={0}>
            <Row label='Domain'>{resource.MOR}</Row>
            <Row label='Merchant'>
                {merchant?.name ?? resource.merchantName ?? resource.merchantId ?? '—'}
            </Row>
            <Row label='Transaction Statuses'>{formatStatuses(resource.transactionStatuses)}</Row>
            <Row label='Phone Frequency'>{frequency?.name ?? resource.phoneFrequency}</Row>
            <Row label='Enabled'>{resource.isEnabled ? 'Yes' : 'No'}</Row>
            <Row label='Date Created'>{resource.createdAt}</Row>
            <Row label='Date Updated'>{resource.updatedAt}</Row>
        </Stack>
    );
};

export default memo(WaMessageSetupView);
